import {Injectable} from '@angular/core';
import {ApiService} from "./api.service";
import {BehaviorSubject, Observable} from "rxjs";
import {Category} from '../structure/types/category.class'
import {Onboarding} from '../structure/types/onboarding.class'
import {StorageService} from "./storage.service";
import {CacheKeys} from "../structure/enums/cache-keys.class";
import {Article} from "../structure/types/article.class";
import {County} from "../structure/types/county.class";
import {CrimeType} from "../structure/types/crime-type.class";
import {Mood} from "../structure/types/mood.class";
import {Feeling} from "../structure/types/Feeling.class";

@Injectable({
    providedIn: 'root'
})
export class ContentService {

    _categories: any = {};
    _articles: any = {};
    _onboarding: any = {};

    _categories$ = new BehaviorSubject(this._categories);
    _articles$ = new BehaviorSubject(this._articles);
    _onboarding$ = new BehaviorSubject(this._onboarding);

    set categories(value: Observable<any>) {
        this._categories = value;
        this._categories$.next(this._categories);
    }

    get categories() { return this._categories$.asObservable() }

    set articles(value: Observable<any>) {
        this._articles = value;
        this._articles$.next(this._articles);
    }

    get articles() { return this._articles$.asObservable()}

    set onboarding(value: Observable<any>) {
        this._onboarding = value;
        this._onboarding$.next(this._onboarding);
    }

    get onboarding() {return this._onboarding$.asObservable()}

    counties: any = {}

    constructor(
        private apiService: ApiService,
        private storageService: StorageService
    ) {
    }

    async init() {
        await Promise.all([
            this.getCountiesData(),
        ]);
    }

    /** Get Categories Request */
    getCategories() {
        return this.apiService.doRequest('categories', 'GET', [])
    }

    /** Get Crime Types Request */
    getCrimeTypes() {
        return this.apiService.doRequest('crime-types', 'GET', [])
    }

    /** Get All Counties Request */
    getCounties(countryId: any) {
        let params = (countryId) ?  {countryId: countryId} : {};
        return this.apiService.doRequest('counties', 'GET', params)
    }

    /** Get Category Articles Request */
    getCategoryArticles(categoryId: any) {
        return this.apiService.doRequest('categories/' + categoryId + '/articles', 'GET', [])
    }

    getRecommendedArticles() {
        return this.apiService.doRequest('articles-recommended', 'GET', [])
    }

    getWellbeingCategoryArticles() {
        return this.apiService.doRequest('categories/mood-wellbeing', 'GET', [])
    }


    /**
     * Get Article By ID Request - Simple Request
     */
    getArticleById(articleId: any) {
        return this.apiService.doRequest('articles/' + articleId, 'GET', [])
    }

    /**
     * Get Article By Slug Request - Simple Request
     */
    getArticleBySlug(slug: any) {
        return this.apiService.doRequest('articles/slug/' + slug, 'GET', [])
    }

    /**
     * Get RiskAssessment By Tag Request - Simple Request
     */
    getRiskAssessmentByTag(tag: any) {
        return this.apiService.doRequest('risk-assessment/' + tag, 'GET', [])
    }

    /**
     * Get Guides By Tag Request - Simple Request
     */
    getGuides(params:any = []) {
        return this.apiService.doRequest('guides', 'GET', params)
    }

    getRecommendedGuides() {
        return this.apiService.doRequest('guides-recommended', 'GET', [])
    }

    getGuide(id: number) {
        return this.apiService.doRequest('guides/' + id, 'GET', [])
    }

    /**
     * The common category page. Deliberately uncached: filters are dynamic and unlock
     * state changes between visits, so a card that just became visible must show up on
     * the next fetch.
     */
    getResources(params: {
        category?: string;
        subcategory?: string;
        type?: string;
        status?: string;
        crime_type_ids?: string;
        search?: string;
        saved?: boolean;
        page?: number;
        per_page?: number;
    }) {
        // setUrlParams() serialises every key, so an absent subcategory would go out as the
        // literal `subcategory=undefined` and 422 as an invalid slug. Only send set keys.
        const query: any = {};
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                // Laravel's `boolean` rule rejects the strings "true"/"false"; send 1/0.
                query[key] = typeof value === 'boolean' ? (value ? 1 : 0) : value;
            }
        });
        return this.apiService.doRequest('resources', 'GET', query)
    }

    sendGuideStepResult(step_no: number, data: Record<string, any> | null, guide_id: number) {
        let params = {
            step_no: step_no,
            data: data,
            guide_id: guide_id
        }
        return this.apiService.doRequest('guides-result', 'POST', params)
    }


    /**
     * Get Categories Request - Simple Request
     */
    getOnboarding() {
        return this.apiService.doRequest('onboarding-pages', 'GET', [])
    }

    /**
     * Get Categories Request
     * @param categoryId
     * @param reset
     */
    async getCategoryArticleData(categoryId: any, reset: boolean = true) {
        const lang: string = await this.storageService.get('lang') ?? 'en';
        const cacheKey = `${categoryId}-${CacheKeys.ARTICLES}_${lang}`;
        let items: any = [];

        if (!reset) {
            items = await this.storageService.get(cacheKey);
        }

        if (reset || items === null) {
            const response = await this.getCategoryArticles(categoryId);

            let itemsResponse: Article[] = response.data;
            if (itemsResponse.length > 0) {
                items = itemsResponse;
            }

            this.storageService.set(cacheKey, items);
        }

        return items;
    }

    /**
     * Get Categories Request
     * @param reset
     */
    async getCategoriesData(reset: boolean = false) {
        const lang: string = await this.storageService.get('lang') ?? 'en';
        return this.getCachedCollection<Category>(
            `${CacheKeys.CATEGORIES}_${lang}`,
            () => this.getCategories(),
            {
                reset,
                assignTo: 'categories',
            }
        );
    }


    /**
     * Get Onboarding Pages Request
     * @param reset
     */
    async getOnboardingData(reset: boolean = false) {
        const lang: string = await this.storageService.get('lang') ?? 'en';
        return this.getCachedCollection<Onboarding>(
            `${CacheKeys.ONBOARDING}_${lang}`,
            () => this.getOnboarding(),
            {
                reset,
                assignTo: 'onboarding',
            }
        );
    }

    /**
     * Get Counties Request
     * @param reset
     */
    async getCountiesData(reset: boolean = false) {
        return this.getCachedCollection<County>(
            CacheKeys.COUNTIES,
            () => this.getCounties(null),
            {
                reset,
                assignTo: 'counties',
            }
        );
    }


    /**
     * Get Crime Types Request (cached per language, as names are localized server-side)
     * @param reset
     */
    async getCrimeTypesData(reset: boolean = false) {
        const lang: string = await this.storageService.get('lang') ?? 'en';
        return this.getCachedCollection<CrimeType>(
            `${CacheKeys.CRIME_TYPES}_${lang}`,
            () => this.getCrimeTypes(),
            { reset }
        );
    }


    /**
     * Get Moods Request
     * @param reset
     */
    async getMoodsData(reset: boolean = false) {
        return this.getCachedCollection<Mood>(
            CacheKeys.MOODS,
            () => this.getMoods(),
            { reset }
        );
    }


    /**
     * Get Feelings Request
     * @param reset
     */
    async getFeelingsData(reset: boolean = false) {
        return this.getCachedCollection<Feeling>(
            CacheKeys.FEELINGS,
            () => this.getFeelings(),
            { reset }
        );
    }

    /** Get Recommended Articles Data */
    async getRecommendedArticlesData(reset: boolean = false) {
        const lang: string = await this.storageService.get('lang') ?? 'en';
        return this.getCachedCollection<Article>(
            `${CacheKeys.RECOMMENDED_ARTICLES}_${lang}`,
            () => this.getRecommendedArticles(),
            { reset }
        );
    }


    /**
     * Do Rate Article Request
     */
    rateArticle(articleId: any, isPositive: number = 0) {
        let params = {
            article_id: articleId,
            is_positive: isPositive,
        }
        return this.apiService.doRequest('article-rate', 'POST', params)
    }

    /**
     * Do Feedback Article Rating Request
     * @param articleToken
     * @param content
     */
    feedbackArticle(articleToken: any, content: string = '') {
        let params = {
            token: articleToken,
            content: content
        }
        return this.apiService.doRequest('article-feedback', 'POST', params)
    }


    /**
     * Do Rate Guide Request
     */
    rateGuide(guideId: any, type: string = 'positive') {
        let params = {
            guide_id: guideId,
            type: type,
        }
        return this.apiService.doRequest('guide-rate', 'POST', params)
    }

    /**
     * Do Feedback Article Rating Request
     * @param guideToken
     * @param feedback
     */
    feedbackGuide(guideToken: any, feedback: string = '') {
        let params = {
            token: guideToken,
            feedback: feedback
        }
        return this.apiService.doRequest('guide-feedback', 'POST', params)
    }

    /**
     * Track Page Views
     * @param url
     */
    pageView(url: string) {
        return this.apiService.doRequest('view', 'POST', {url: url})
    }


    /**
     * Get Pages By Slug Request - Simple Request
     */
    getPageBySlug(slug: any) {
        return this.apiService.doRequest('page/slug/' + slug, 'GET', [])
    }

    /**
     * Get Moods Request - Simple Request
     */
    getMoods() {
        return this.apiService.doRequest('moods', 'GET', [])
    }

    /**
     * Get Feelings Request - Simple Request
     */
    getFeelings() {
        return this.apiService.doRequest('feelings', 'GET', [])
    }

    /**
     * Get Entries Request - Simple Request
     */
    getEntries(month: number | null = null, year: number | null = null) {

        let params: any = {};

        if (month !== null) {
            params.month = month;
        }

        if (year !== null) {
            params.year = year;
        }

        return this.apiService.doRequest('member/entry', 'GET', params);
    }

    /**
     * Get entries within an inclusive date range (single request, server-filtered).
     */
    getEntriesForRange(startDate: string, endDate: string) {
        return this.apiService.doRequest('member/entry', 'GET', {
            start_date: startDate,
            end_date: endDate,
        });
    }

    getEntriesStatistics(month: number | null = null, year: number | null = null) {
        let params: any = {};

        if (month !== null) {
            params.month = month;
        }

        if (year !== null) {
            params.year = year;
        }

        return this.apiService.doRequest('member/entry/monthly-statistics', 'GET', params);
    }

    getAllEntriesStatistics() {
        let params: any = {};
        return this.apiService.doRequest('member/entry/all-statistics', 'GET', params);
    }

    /**
     * Get entry from that day
     * @param day
     */
    getEntry(day: any) {
        return this.apiService.doRequest('member/entry', 'GET', {day: day});
    }

    /** Get active live incident banner for logged in member */
    getLiveIncidentBanner() {
        return this.apiService.doRequest('live-incident/banner', 'GET', []);
    }

    /** Get live incident article by incident id */
    getLiveIncidentArticleById(id: number) {
        return this.apiService.doRequest(`live-incident/${id}/article`, 'GET', []);
    }

    /** Get short by slug */
    getShortBySlug(slug: string) {
        return this.apiService.doRequest('shorts/slug/' + slug, 'GET', []);
    }

    /** Get all service regions */
    getServiceRegions() {
        return this.apiService.doRequest('service-regions', 'GET', []);
    }

    /** Get a service region by slug/id */
    getServiceRegion(slug: string) {
        return this.apiService.doRequest('service-regions/' + slug, 'GET', []);
    }

    /** Get service regions filtered by the authenticated member's county */
    getServiceRegionsForMember() {
        return this.apiService.doRequest('service-regions/for-member', 'GET', []);
    }

    /** Get all service categories */
    getServiceCategories() {
        return this.apiService.doRequest('service-categories', 'GET', []);
    }

    /** Get organisations by region slug */
    getOrganisationsByRegion(slug: string) {
        return this.apiService.doRequest('organisations/region/' + slug, 'GET', []);
    }



    /**
     * Clear all language-dependent public content cache entries.
     * Call this when the user changes language, before reloading the app.
     * Does NOT touch auth tokens, user data, diary/mood records, or app settings.
     */
    async clearContentCache(): Promise<void> {
        const allKeys: string[] = await this.storageService.keys();
        for (const key of allKeys) {
            if (
                // CATEGORIES_lang, CATEGORIES, CATEGORIES_cached_at
                key.startsWith(`${CacheKeys.CATEGORIES}_`) ||
                key === CacheKeys.CATEGORIES ||
                key === `${CacheKeys.CATEGORIES}_cached_at` ||
                // ONBOARDING_lang, ONBOARDING, ONBOARDING_cached_at
                key.startsWith(`${CacheKeys.ONBOARDING}_`) ||
                key === CacheKeys.ONBOARDING ||
                key === `${CacheKeys.ONBOARDING}_cached_at` ||
                // MOODS, MOODS_cached_at
                key === CacheKeys.MOODS ||
                key === `${CacheKeys.MOODS}_cached_at` ||
                // FEELINGS, FEELINGS_cached_at
                key === CacheKeys.FEELINGS ||
                key === `${CacheKeys.FEELINGS}_cached_at` ||
                // recommended_articles_lang, recommended_articles, recommended_articles_cached_at
                key.startsWith(`${CacheKeys.RECOMMENDED_ARTICLES}_`) ||
                key === CacheKeys.RECOMMENDED_ARTICLES ||
                key === `${CacheKeys.RECOMMENDED_ARTICLES}_cached_at` ||
                // guide_categories_type_lang (always keyed with lang)
                key.startsWith(`${CacheKeys.GUIDE_CATEGORIES}_`) ||
                // {categoryId}-ARTICLES_lang and {categoryId}-ARTICLES (dynamic keys)
                key.includes(`-${CacheKeys.ARTICLES}`)
            ) {
                await this.storageService.remove(key);
            }
        }
    }

    // Get collections and Cache them
    private readonly defaultCacheTtl = 24 * 60 * 60 * 1000;

    private async fetchAndCache<T>(key: string, requestFn: () => Promise<any>,): Promise<T[]>
    {
        const response = await requestFn();
        const items: T[] = response?.data ?? [];
        await this.storageService.set(key, items);
        await this.storageService.set(`${key}_cached_at`, Date.now());
        return items;
    }

    private async getCachedCollection<T>(
        key: string,
        requestFn: () => Promise<any>,
        options?: {
            reset?: boolean;
            ttlMs?: number;
            assignTo?: 'categories' | 'onboarding' | 'counties' | null;
        }
    ): Promise<T[]>
    {
        const reset = options?.reset ?? false;
        const ttlMs = options?.ttlMs ?? this.defaultCacheTtl;
        const assignTo = options?.assignTo ?? null;

        let items: T[] | null = null;

        if (!reset) {
            const cached = await this.storageService.get(key);
            const cachedAt = await this.storageService.get(`${key}_cached_at`);

            const isValid =
                cached !== null &&
                cachedAt !== null &&
                Date.now() - cachedAt < ttlMs;

            if (isValid) {
                items = cached;
            }
        }

        if (items === null) {
            items = await this.fetchAndCache<T>(key, requestFn);
        }

        if (assignTo) {
            this[assignTo] = items;
        }

        return items;
    }

}
